import type {
  PermissionResolver,
  RolePermissionMap,
} from "@its-engineering-and-development/rbac-react";

type DecodedJwtPayload = {
  roles?: unknown;
};

function decodeJwtPayload(token: string): DecodedJwtPayload | null {
  const tokenParts = token.split(".");

  if (tokenParts.length < 2) {
    return null;
  }

  try {
    const base64 = tokenParts[1].replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = atob(base64);
    return JSON.parse(jsonPayload) as DecodedJwtPayload;
  } catch {
    return null;
  }
}

function isStringArray(value: unknown): value is string[] {
  return (
    Array.isArray(value) && value.every((item) => typeof item === "string")
  );
}

function extractRoles(payload: DecodedJwtPayload | null): string[] {
  if (!payload?.roles) {
    return [];
  }

  if (isStringArray(payload.roles)) {
    return payload.roles;
  }

  if (typeof payload.roles !== "object" || payload.roles === null) {
    return [];
  }

  const nestedRoleValues = Object.values(payload.roles).filter(isStringArray);
  return nestedRoleValues.flat();
}

export class StaticRoleResolver implements PermissionResolver {
  constructor(private readonly roleMap: RolePermissionMap) {}

  async resolve(token: string): Promise<string[]> {
    const payload = decodeJwtPayload(token);
    const roles = extractRoles(payload);
    const actions = new Set<string>();

    for (const role of roles) {
      const rolePermissions = this.roleMap[role];

      if (!rolePermissions) {
        continue;
      }

      for (const action of rolePermissions) {
        actions.add(action);
      }
    }

    return Array.from(actions);
  }
}
